import React, { useState } from 'react';
import { AthleteProfile } from '../types';
import { 
  ShieldCheck, 
  Crown, 
  X, 
  Lock, 
  Check, 
  ExternalLink,
  Sparkles,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  RotateCcw,
  Key,
  WifiOff,
  AlertOctagon,
  ChevronDown,
  ChevronUp,
  Terminal,
  HelpCircle
} from 'lucide-react';

interface ProCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AthleteProfile;
  onPaymentSuccess: (updatedProfilePartial: Partial<AthleteProfile>) => void;
  merchantPaypalEmail?: string;
  merchantBankDetails?: {
    bankName: string;
    accountHolder: string;
    accountNumber: string;
    routingNumber: string;
    swiftIban: string;
  };
}

export interface GranularCheckoutError {
  category: 'NETWORK' | '405_METHOD' | 'CREDENTIALS' | 'STRIPE_API' | 'INVALID_URL' | 'SERVER_ERROR';
  badgeLabel: string;
  title: string;
  description: string;
  technicalDetails?: string;
  statusCode?: number;
  troubleshootingSteps: string[];
}

/**
 * Granular error classification parser for Stripe Checkout API errors.
 * Inspects status codes, network errors, Stripe parameter mismatches, and credential statuses.
 */
function parseCheckoutError(err: any, status?: number, responseData?: any): GranularCheckoutError {
  const errMsg = (responseData?.error || responseData?.message || err?.message || String(err)).toLowerCase();
  const rawMsg = responseData?.error || responseData?.message || err?.message || String(err);

  // 1. 405 Method Not Allowed
  if (status === 405 || errMsg.includes('405') || errMsg.includes('method not allowed')) {
    return {
      category: '405_METHOD',
      badgeLabel: 'HTTP 405 Method Mismatch',
       title: 'Checkout Request Method Rejected (405)'+ errMsg, 
      description: 'The server endpoint received a request method that was not processed as expected. The client will automatically fall back to alternative routes with full payload headers.',
      technicalDetails: `Status: 405 | Endpoint: /api/create-checkout-session | Allowed Methods: ${responseData?.allowedMethods?.join(', ') || 'POST, GET, OPTIONS'}\nDetails: ${rawMsg}`,
      statusCode: 405,
      troubleshootingSteps: [
        'Click "Retry Stripe Checkout" below to trigger a fresh POST request with forced JSON headers.',
        'Check if browser extensions or proxy settings are stripping POST bodies.',
      ],
    };
  }

  // 2. Missing or Invalid Stripe Secret Key
  if (
    (status === 500 && (errMsg.includes('secret key') || errMsg.includes('stripe credentials') || responseData?.code === 'STRIPE_SECRET_KEY_MISSING')) ||
    errMsg.includes('secret key') || errMsg.includes('api_key') || errMsg.includes('sk_live') || errMsg.includes('sk_test')
  ) {
    return {
      category: 'CREDENTIALS',
      badgeLabel: 'Stripe Key Configuration',
      title: 'Stripe Secret Key Missing or Unconfigured',
      description: 'The server requires a valid Stripe Secret Key (STRIPE_SECRET_KEY) to securely generate a checkout session.',
      technicalDetails: `Status: ${status || 500} | Error: ${rawMsg}`,
      statusCode: status || 500,
      troubleshootingSteps: [
        'Verify that STRIPE_SECRET_KEY is defined in the project settings or environment.',
        'Ensure the Stripe API key begins with sk_live_ or sk_test_.',
      ],
    };
  }

  // 3. Network or Offline error
  if (
    err instanceof TypeError ||
    errMsg.includes('failed to fetch') ||
    errMsg.includes('networkerror') ||
    errMsg.includes('network connection') ||
    errMsg.includes('offline') ||
    errMsg.includes('econnrefused')
  ) {
    return {
      category: 'NETWORK',
      badgeLabel: 'Network Connectivity',
      title: 'Unable to Connect to Checkout Server',
      description: 'The browser could not establish a network connection to the Playground checkout service. Please check your internet connection.',
      technicalDetails: `Client Network Error: ${err?.name || 'TypeError'}: ${rawMsg}`,
      troubleshootingSteps: [
        'Check your Wi-Fi or cellular data connection.',
        'Disable strict ad blockers, tracking preventers, or corporate VPNs that might block API requests.',
      ],
    };
  }

  // 4. Stripe API / Account / Parameter validation error
  if (status === 400 || errMsg.includes('stripe') || errMsg.includes('payment_method_types') || errMsg.includes('managed_payments')) {
    return {
      category: 'STRIPE_API',
      badgeLabel: 'Stripe Gateway Notice',
      title: 'Stripe Payment Gateway Parameter Notice',
      description: rawMsg || 'Stripe API returned a parameter or account configuration notice.',
      technicalDetails: `Status: ${status || 400} | Details: ${JSON.stringify(responseData?.details || rawMsg, null, 2)}`,
      statusCode: status || 400,
      troubleshootingSteps: [
        'Retry the checkout process with a standard payment method.',
        'Review your Stripe Dashboard for account status or live mode enablement.',
      ],
    };
  }

  // 5. Invalid URL returned
  if (errMsg.includes('invalid url') || errMsg.includes('redirect url') || errMsg.includes('protocol')) {
    return {
      category: 'INVALID_URL',
      badgeLabel: 'Redirect Validation',
      title: 'Invalid Checkout Redirect URL',
      description: 'The server responded with an unrecognized or non-HTTP redirect URL.',
      technicalDetails: `URL Validation Error: ${rawMsg}`,
      troubleshootingSteps: [
        'Try restarting the checkout session to fetch a fresh Stripe URL.',
      ],
    };
  }

  // Generic fallback
  return {
    category: 'SERVER_ERROR',
    badgeLabel: `Server Error (${status || 'API'})`,
    title: 'Checkout Session Initialization Error',
    description: rawMsg || 'An unexpected error occurred while initializing Stripe Checkout.',
    technicalDetails: `Status: ${status || 'Unknown'} | Raw: ${rawMsg}`,
    statusCode: status,
    troubleshootingSteps: [
      'Click "Retry Stripe Checkout" to re-attempt the session creation.',
      'If the error persists, contact Playground League support.',
    ],
  };
}

export const ProCheckoutModal: React.FC<ProCheckoutModalProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'monthly_pro' | 'lifetime_verified'>('monthly_pro');
  
  // Processing & Redirect state
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRedirectingOverlay, setIsRedirectingOverlay] = useState(false);
  const [hostedCheckoutUrl, setHostedCheckoutUrl] = useState<string | null>(null);
  
  // Granular error state
  const [granularError, setGranularError] = useState<GranularCheckoutError | null>(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  if (!isOpen) return null;

  const planNumericAmount = selectedPlan === 'monthly_pro' ? 9.99 : 19.99;
  const planAmountStr = selectedPlan === 'monthly_pro' ? '$9.99' : '$19.99';

  /**
   * Main Stripe Hosted Checkout flow:
   * 1. Invokes a single fetch call to the backend session endpoint (/api/create-checkout-session)
   * 2. Wraps everything in a robust try-catch block for comprehensive network error handling
   * 3. Categorizes any failure (405 method error, secret key missing, network drop, etc.)
   * 4. Verifies that the returned URL is a fully qualified absolute URL (http:// or https://)
   * 5. Logs the exact URL object before calling window.location.assign()
   * 6. Never triggers premature client-side success transitions
   */
  const handleProceedToStripe = async () => {
    setIsProcessing(true);
    setIsRedirectingOverlay(true);
    setGranularError(null);
    setShowTechnicalDetails(false);

    const clientOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const requestPayload = {
      userId: user.id,
      userEmail: user.email || 'athlete@playgroundleague.com',
      planType: selectedPlan,
      amount: planNumericAmount,
      origin: clientOrigin,
      returnUrl: `${clientOrigin}/?session_id={CHECKOUT_SESSION_ID}&pro_success=true`,
    };

    const endpointUrl = '/api/create-checkout-session';
    const fetchMethod = 'POST';
    const fetchHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Explicitly capture and output exact 'fetch' method, URL, and headers immediately before request is made
    console.log('[Stripe Checkout Pre-Request] Outgoing fetch parameters for /api/create-checkout-session:', {
      method: fetchMethod,
      url: endpointUrl,
      headers: fetchHeaders,
      payload: requestPayload,
      matchesServerPostConfig: fetchMethod === 'POST' && fetchHeaders['Content-Type'] === 'application/json',
      timestamp: new Date().toISOString(),
    });

    try {
      let res: Response | null = null;
      let lastNetworkErr: any = null;

      // Primary Attempt: /api/create-checkout-session (POST)
      try {
        res = await fetch(endpointUrl, {
          method: fetchMethod,
          headers: fetchHeaders,
          body: JSON.stringify(requestPayload),
        });
      } catch (err1: any) {
        lastNetworkErr = err1;
        console.warn('[Stripe Checkout Client] Primary endpoint unreachable, attempting alias /api/stripe/create-checkout-session:', err1);
      }

      // Secondary Attempt if first failed or returned 405: /api/stripe/create-checkout-session
      if (!res || (!res.ok && res.status === 405)) {
        try {
          res = await fetch('/api/stripe/create-checkout-session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify(requestPayload),
          });
        } catch (err2: any) {
          lastNetworkErr = err2;
          console.warn('[Stripe Checkout Client] Secondary endpoint unreachable, attempting alias /api/stripe/checkout-session:', err2);
        }
      }

      // Tertiary Attempt if still 405 or failed: /api/stripe/checkout-session
      if (!res || (!res.ok && res.status === 405)) {
        try {
          res = await fetch('/api/stripe/checkout-session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify(requestPayload),
          });
        } catch (err3: any) {
          lastNetworkErr = err3;
        }
      }

      // If all network requests failed completely (e.g. offline)
      if (!res) {
        throw lastNetworkErr || new Error('Network connection failed. Unable to reach any checkout endpoints.');
      }

      // Handle non-OK HTTP status codes
      if (!res.ok) {
        let errJson: any = {};
        try {
          errJson = await res.json();
        } catch (parseErr) {
          // Response body was not JSON
        }

        console.error('[Stripe Checkout Client] Backend session creation returned error:', {
          status: res.status,
          statusText: res.statusText,
          errorResponse: errJson,
        });

        const classifiedError = parseCheckoutError(null, res.status, errJson);
        setGranularError(classifiedError);
        setIsProcessing(false);
        setIsRedirectingOverlay(false);
        return;
      }

      const sessionData = await res.json();
      console.log('[Stripe Checkout Client] Backend session data received:', sessionData);

      const rawUrl = sessionData?.url;
      if (!rawUrl || typeof rawUrl !== 'string') {
        const classifiedError = parseCheckoutError(new Error('Stripe backend did not return a valid checkout URL. Please try again.'), res.status, sessionData);
        setGranularError(classifiedError);
        setIsProcessing(false);
        setIsRedirectingOverlay(false);
        return;
      }

      // Verify that the URL is a fully qualified absolute URL
      let targetUrl: URL;
      try {
        targetUrl = new URL(rawUrl, clientOrigin);
      } catch (urlParseErr: any) {
        const classifiedError = parseCheckoutError(new Error(`Invalid Stripe Checkout URL format received from server: "${rawUrl}"`), res.status);
        setGranularError(classifiedError);
        setIsProcessing(false);
        setIsRedirectingOverlay(false);
        return;
      }

      const absoluteUrl = targetUrl.href;
      if (!absoluteUrl.startsWith('http://') && !absoluteUrl.startsWith('https://')) {
        const classifiedError = parseCheckoutError(new Error(`Invalid URL protocol: "${absoluteUrl}". Stripe Checkout requires an HTTP/HTTPS absolute URL.`), res.status);
        setGranularError(classifiedError);
        setIsProcessing(false);
        setIsRedirectingOverlay(false);
        return;
      }

      // Required logging of exact URL object received from the backend before redirection
      console.log('[Stripe Checkout Navigation] Exact URL object received from backend before redirect:', {
        rawUrl,
        resolvedAbsoluteUrl: absoluteUrl,
        urlObject: {
          href: targetUrl.href,
          origin: targetUrl.origin,
          protocol: targetUrl.protocol,
          host: targetUrl.host,
          hostname: targetUrl.hostname,
          port: targetUrl.port,
          pathname: targetUrl.pathname,
          search: targetUrl.search,
          hash: targetUrl.hash,
        },
        sessionId: sessionData.sessionId,
        userId: user.id,
        plan: selectedPlan,
        amount: planNumericAmount,
        timestamp: new Date().toISOString(),
      });

      setHostedCheckoutUrl(absoluteUrl);

      // Perform top-level or window redirection safely
      try {
        if (window.top && window.top !== window) {
          window.top.location.assign(absoluteUrl);
        } else {
          window.location.assign(absoluteUrl);
        }
      } catch (redirectErr) {
        console.warn('[Stripe Checkout Client] Top-frame navigation blocked, executing direct location.assign:', redirectErr);
        window.location.assign(absoluteUrl);
      }
    } catch (err: any) {
      console.error('[Stripe Checkout Error Caught]:', {
        errorName: err?.name,
        errorMessage: err?.message,
        stack: err?.stack,
        userId: user.id,
        selectedPlan,
        amount: planNumericAmount,
        timestamp: new Date().toISOString(),
      });

      const classifiedError = parseCheckoutError(err);
      setGranularError(classifiedError);
      setIsProcessing(false);
      setIsRedirectingOverlay(false);
    }
  };

  const getErrorIcon = (category: GranularCheckoutError['category']) => {
    switch (category) {
      case 'CREDENTIALS':
        return <Key className="w-5 h-5 text-amber-400 shrink-0" />;
      case 'NETWORK':
        return <WifiOff className="w-5 h-5 text-sky-400 shrink-0" />;
      case '405_METHOD':
        return <AlertOctagon className="w-5 h-5 text-rose-400 shrink-0" />;
      case 'STRIPE_API':
        return <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0" />;
    }
  };

  return (
    <>
      {/* FULL-SCREEN REDIRECTING TO STRIPE OVERLAY */}
      {isRedirectingOverlay && (
        <div id="stripe-redirect-overlay" className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-xl flex flex-col items-center justify-center p-6 text-center animate-fadeIn select-none">
          <div className="max-w-md w-full bg-indigo-950/90 border-2 border-amber-400/80 rounded-[2.5rem] p-8 space-y-6 shadow-2xl shadow-amber-400/20 text-white relative overflow-hidden">
            {/* Ambient background glow */}
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-amber-400/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />

            {/* Glowing animated Lock badge */}
            <div className="relative mx-auto w-20 h-20 flex items-center justify-center">
              <div className="absolute inset-0 rounded-3xl bg-amber-400/30 animate-ping" />
              <div className="relative w-20 h-20 rounded-3xl bg-gradient-to-tr from-amber-400 via-amber-300 to-yellow-400 text-black flex items-center justify-center shadow-xl shadow-amber-400/40">
                <Lock className="w-10 h-10 stroke-[2.5]" />
              </div>
            </div>

            {/* Title & Status */}
            <div className="space-y-2">
              <div className="inline-flex items-center space-x-2 bg-amber-400/20 border border-amber-400/40 px-3 py-1 rounded-full text-xs font-black uppercase font-mono text-amber-300">
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-400" />
                <span>Connecting to Stripe...</span>
              </div>
              <h3 className="text-2xl font-black italic uppercase tracking-tight text-white">
                Redirecting to Stripe
              </h3>
              <p className="text-xs text-indigo-200 leading-relaxed">
                Establishing 256-bit encrypted connection to Stripe Checkout servers. You will be redirected automatically to complete payment.
              </p>
            </div>

            {/* Animated progress bar */}
            <div className="w-full bg-white/10 rounded-full h-2.5 overflow-hidden p-0.5 border border-white/20">
              <div className="bg-gradient-to-r from-amber-400 via-lime-400 to-yellow-300 h-full rounded-full animate-pulse w-4/5 transition-all duration-1000" />
            </div>

            {/* Fallback Direct Redirect Button */}
            {hostedCheckoutUrl ? (
              <div className="pt-2 space-y-2">
                <a
                  href={hostedCheckoutUrl}
                  target="_top"
                  className="w-full py-3.5 px-4 bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-300 hover:to-yellow-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg transition flex items-center justify-center space-x-2"
                >
                  <span>Click here if not redirected in 3 seconds</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            ) : (
              <p className="text-[11px] text-indigo-300/70 font-mono">
                Generating live checkout session token...
              </p>
            )}

            {/* Cancel / Dismiss Overlay option */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsRedirectingOverlay(false);
                  setIsProcessing(false);
                }}
                className="text-xs text-indigo-300 hover:text-white underline transition"
              >
                Cancel redirect and return to options
              </button>
            </div>

            {/* Trust badge footer */}
            <div className="pt-2 border-t border-white/10 flex items-center justify-center space-x-4 text-[11px] text-indigo-300/80 font-mono">
              <span className="flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-lime-400" />
                PCI-DSS Level 1
              </span>
              <span>•</span>
              <span>256-Bit SSL Encrypted</span>
            </div>
          </div>
        </div>
      )}

      <div id="pro-checkout-modal" className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
        <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-950 text-white rounded-[2.5rem] border-2 border-amber-400/80 shadow-2xl w-full max-w-xl overflow-hidden p-6 sm:p-8 my-6 relative space-y-6">
          {/* Close Button */}
          <button
            id="pro-modal-close-btn"
            onClick={onClose}
            className="absolute top-5 right-5 p-2 text-indigo-300 hover:text-white rounded-full bg-indigo-900/80 border border-white/10 transition z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Modal Header */}
          <div className="text-center space-y-2 pt-1">
            <div className="w-14 h-14 rounded-3xl bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 text-black mx-auto flex items-center justify-center shadow-xl shadow-amber-400/20 animate-pulse">
              <Crown className="w-7 h-7 stroke-[2.5]" />
            </div>
            <div className="inline-flex items-center space-x-2 bg-amber-400/20 border border-amber-400/40 px-3 py-0.5 rounded-full text-[10px] font-black uppercase font-mono text-amber-300">
              <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
              <span>Official Stripe Hosted Checkout</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tight text-white">
              Upgrade to Playground PRO
            </h2>
            <p className="text-xs text-indigo-200/80 max-w-md mx-auto font-medium">
              You will be taken directly to Stripe's secure checkout. Once payment is completed, you'll be brought right back with your PRO status unlocked!
            </p>
          </div>

          {/* PLAN SELECTION & DIRECT STRIPE CHECKOUT ACTION */}
          <div className="space-y-5">
            {/* PLAN SELECTION CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                id="plan-monthly-btn"
                type="button"
                onClick={() => setSelectedPlan('monthly_pro')}
                className={`p-4 rounded-2xl border text-left transition relative ${
                  selectedPlan === 'monthly_pro'
                    ? 'bg-indigo-900/90 border-amber-400 ring-2 ring-amber-400/50 shadow-lg shadow-amber-400/10'
                    : 'bg-indigo-950/60 border-white/10 hover:border-white/20'
                }`}
              >
                {selectedPlan === 'monthly_pro' && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 bg-amber-400 text-black text-[9px] font-black uppercase font-mono rounded">
                    POPULAR
                  </span>
                )}
                <div className="text-xs font-black italic uppercase text-indigo-300 font-mono">Monthly Subscription</div>
                <div className="text-2xl font-black text-white mt-1">$9.99 <span className="text-xs text-indigo-200 font-normal">/ month</span></div>
                <div className="text-[11px] text-indigo-200/80 mt-1.5">Full PRO scouting analytics, badge, & unlimited 1080p video highlights.</div>
              </button>

              <button
                id="plan-lifetime-btn"
                type="button"
                onClick={() => setSelectedPlan('lifetime_verified')}
                className={`p-4 rounded-2xl border text-left transition relative ${
                  selectedPlan === 'lifetime_verified'
                    ? 'bg-indigo-900/90 border-amber-400 ring-2 ring-amber-400/50 shadow-lg shadow-amber-400/10'
                    : 'bg-indigo-950/60 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="text-xs font-black italic uppercase text-indigo-300 font-mono">Lifetime Pro Pass</div>
                <div className="text-2xl font-black text-lime-400 mt-1">$19.99 <span className="text-xs text-indigo-200 font-normal">one-time</span></div>
                <div className="text-[11px] text-indigo-200/80 mt-1.5">Permanent Gold Verified Athlete Badge & lifetime PRO perks forever.</div>
              </button>
            </div>

            {/* INCLUDED PRO FEATURES CHECKLIST */}
            <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-2.5">
              <div className="text-xs font-black uppercase tracking-wider text-amber-300 flex items-center space-x-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                <span>What's Included with Playground PRO:</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-indigo-100">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-lime-400 shrink-0" />
                  <span>Gold Verified Badge on Profile</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-lime-400 shrink-0" />
                  <span>Unlimited 1080p Highlight Reels</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-lime-400 shrink-0" />
                  <span>State & National Filtered Standings</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-lime-400 shrink-0" />
                  <span>AI Scout Reports & Performance Charts</span>
                </div>
              </div>
            </div>

            {/* GRANULAR, USER-FRIENDLY ERROR BANNER WITH ACTIONABLE TROUBLESHOOTING */}
            {granularError && (
              <div id="stripe-granular-error-banner" className="p-4 bg-rose-950/70 border-2 border-rose-500/70 rounded-2xl text-white space-y-3.5 animate-fadeIn shadow-lg shadow-rose-950/40">
                {/* Header with Error Badge & Dismiss */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center space-x-2">
                    {getErrorIcon(granularError.category)}
                    <span className="px-2 py-0.5 bg-rose-500/20 border border-rose-400/40 rounded text-[10px] font-black uppercase font-mono text-rose-300">
                      {granularError.badgeLabel}
                    </span>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => setGranularError(null)} 
                    className="text-rose-300 hover:text-white p-1 rounded-lg transition"
                    title="Dismiss notification"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Error Title & Clear Description */}
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-rose-100 flex items-center gap-1.5">
                    {granularError.title}
                  </h4>
                  <p className="text-xs text-rose-200/90 leading-relaxed">
                    {granularError.description}
                  </p>
                </div>

                {/* Specific Actionable Troubleshooting Steps */}
                {granularError.troubleshootingSteps.length > 0 && (
                  <div className="p-3 bg-black/40 rounded-xl border border-rose-500/30 space-y-1.5 text-xs text-rose-200">
                    <div className="font-bold text-[11px] uppercase tracking-wider text-amber-300 font-mono flex items-center gap-1">
                      <HelpCircle className="w-3.5 h-3.5 text-amber-400" />
                      <span>Recommended Solution:</span>
                    </div>
                    <ul className="space-y-1 pl-4 list-disc text-[11px] text-rose-100/90 leading-relaxed">
                      {granularError.troubleshootingSteps.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Toggleable Technical Details */}
                {granularError.technicalDetails && (
                  <div className="space-y-1 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                      className="text-[11px] text-rose-300 hover:text-rose-100 flex items-center space-x-1 font-mono transition"
                    >
                      <Terminal className="w-3 h-3" />
                      <span>{showTechnicalDetails ? 'Hide technical logs' : 'Show technical details & diagnostics'}</span>
                      {showTechnicalDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>

                    {showTechnicalDetails && (
                      <pre className="p-2.5 bg-black/80 rounded-lg text-[10px] font-mono text-emerald-400 overflow-x-auto border border-white/10 whitespace-pre-wrap">
                        {granularError.technicalDetails}
                      </pre>
                    )}
                  </div>
                )}

                {/* Retry & Secondary Action Buttons */}
                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-rose-500/30">
                  <button
                    id="stripe-granular-retry-btn"
                    type="button"
                    onClick={handleProceedToStripe}
                    disabled={isProcessing}
                    className="py-2.5 px-4 bg-gradient-to-r from-rose-500 via-amber-500 to-yellow-400 hover:from-rose-400 hover:to-yellow-300 text-black font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-1.5 active:scale-95"
                  >
                    <RotateCcw className="w-3.5 h-3.5 stroke-[2.5]" />
                    <span>Retry Stripe Checkout</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setGranularError(null)}
                    className="py-2.5 px-3.5 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl transition"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* DIRECT STRIPE CHECKOUT PRIMARY BUTTON */}
            <div className="space-y-3 pt-1">
              <button
                id="stripe-checkout-direct-btn"
                type="button"
                disabled={isProcessing}
                onClick={handleProceedToStripe}
                className="w-full py-4 bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 hover:from-amber-300 hover:to-yellow-300 text-black font-black italic uppercase text-sm sm:text-base rounded-2xl shadow-xl shadow-amber-400/30 hover:scale-[1.01] active:scale-[0.99] transition flex items-center justify-center space-x-2"
              >
                {isProcessing ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>Connecting to Stripe Checkout...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>Pay {planAmountStr} with Stripe Checkout</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Supported Payment Logos & Stripe Trust Notice */}
              <div className="flex flex-col items-center justify-center space-y-2 text-center">
                <div className="flex items-center justify-center space-x-3 text-xs text-indigo-300/80 font-mono">
                  <span className="px-2 py-0.5 bg-black/40 rounded border border-white/10 font-bold">💳 Cards</span>
                  <span className="px-2 py-0.5 bg-black/40 rounded border border-white/10 font-bold"> Apple Pay</span>
                  <span className="px-2 py-0.5 bg-black/40 rounded border border-white/10 font-bold">Google Pay</span>
                  <span className="px-2 py-0.5 bg-black/40 rounded border border-white/10 font-bold">Link</span>
                </div>
                <p className="text-[11px] text-indigo-300/70 max-w-sm">
                  Redirects directly to Stripe's secure payment servers. Cancel anytime.
                </p>
              </div>
            </div>

            {/* Bottom Security Footer */}
            <div className="flex items-center justify-between text-[11px] text-indigo-300/60 pt-2 border-t border-white/10 font-mono">
              <span className="flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                PCI-DSS Level 1 Encrypted
              </span>
              <span>100% Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
